/*
 * AAIKO navigation drawer, both breakpoints.
 * Figma mobile 1210:25100 (root state) / 1210:25212 (sub state),
 * desktop 3173:19565 (root) / 3173:19628 (sub). Same state machine; the
 * desktop panel starts under the header row (--aaiko-nav-top, measured at
 * open) and the open trigger in the header goes Semibold, as drawn in the
 * open frame's Nav instance (3173:20989).
 *
 * Delegated listeners on the document so a section re-render in the theme
 * editor needs no re-initialisation. No framework, no Horizon core touched.
 */
(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]), select, input, textarea, [tabindex]:not([tabindex="-1"])';
  var lastTrigger = null;

  /* Motion. Kept in step with the transition durations in the drawer's
     stylesheet: the close path has to wait for the exit before re-applying
     `hidden`, because `display: none` cannot be transitioned. This is the
     LONGER of the two exits - the mobile panel's 340ms slide out
     (--aaiko-nav-dur-panel) rather than the desktop 280ms fade - so neither
     breakpoint gets cut off mid-motion. */
  var DUR = 340;
  /* No intent delay. The trigger's hover target is widened in the header's
     stylesheet to the full 44px row plus half the 32px gap either side, so the
     menu is already opening while the cursor is still travelling toward the
     label rather than waiting until it lands on the 14.4px line box. */
  var OPEN_DELAY = 0;
  var CLOSE_DELAY = 220; /* travel time from the header item down into the panel */
  var STAGGER_CAP = 16;

  /* Hover only where hovering is real. Coarse pointers keep the click path. */
  var FINE = window.matchMedia('(hover: hover) and (pointer: fine)');
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  var dismissed = false;
  var hoverTimer = null;
  var closeTimer = null;
  var hideTimer = null;

  function drawer() {
    return document.querySelector('[data-aaiko-nav]');
  }
  function mode(el) {
    return el && el.getAttribute('data-aaiko-nav-mode');
  }
  /* Index every visible row so the stylesheet can stagger them off --i.
     Done in JS rather than nth-child so it covers the root lists, the tab
     panels and the branch sub-lists with one rule. */
  function stagger(list) {
    if (!list) return;
    /* rows live in grouped ULs since the desktop restructure; the edits
       section enters as one unit, like a row */
    var rows = list.querySelectorAll('.aaiko-nav__rows > li, .aaiko-nav__edits');
    if (!rows.length) rows = list.children;
    for (var i = 0; i < rows.length; i++) {
      rows[i].style.setProperty('--i', String(Math.min(i, STAGGER_CAP)));
    }
    /* restart the animation when a list is re-shown */
    list.removeAttribute('data-enter');
    void list.offsetWidth;
    list.setAttribute('data-enter', '');
  }
  function staggerActive(el) {
    if (!el) return;
    var panel = el.querySelector('[role="tabpanel"]:not([hidden])');
    if (panel) stagger(panel.querySelector('[data-nav-list]:not([hidden]), [data-nav-sub]:not([hidden])'));
  }
  function toggles() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-aaiko-nav-toggle]'));
  }
  function isOpen(el) {
    return el && !el.hasAttribute('hidden');
  }
  /* A top-level item can be parked on `#` in the menu editor. Such an item is
     a menu trigger and nothing else - following it would reload the current
     page - so it keeps the old click-opens-the-drawer behaviour. */
  function navigable(node) {
    var href = node && node.getAttribute && node.getAttribute('href');
    return !!(href && href.charAt(0) !== '#');
  }

  /* Drawn desktop frames start the overlay UNDER the header row (y 76 in the
     frame: 32 announcement + 44 row), which keeps the header visible and
     interactive above the scrim. Measured, not hardcoded: at the top of the
     page the announcement still counts, scrolled down only the sticky row
     does. Mobile ignores the property (its overlay keeps inset: 0). */
  function placeUnderHeader(el) {
    var header = document.querySelector('.aaiko-header');
    if (!header) return;
    var bottom = Math.max(0, header.getBoundingClientRect().bottom);
    el.style.setProperty('--aaiko-nav-top', bottom + 'px');
  }

  /* The label a header trigger is matched against. The attribute, not the
     rendered text: the open tab also carries the visually hidden "Alles
     bekijken" that goes with its arrow, so textContent no longer is the
     merchant's label. Falls back to the text for a tab rendered before the
     attribute existed. */
  function tabLabel(tab) {
    if (!tab) return null;
    var label = tab.getAttribute('data-nav-tab-label');
    if (label) return label.trim().toLowerCase();
    return tab.textContent.trim().toLowerCase();
  }

  /* Drawn Nav instance in the open frames (3173:20989): the trigger whose tab
     is open reads Switzer Semibold. Synced by label, same contract tabFor
     uses, and cleared with the menu. */
  function syncTriggers(el) {
    var open_ = el && el.hasAttribute('data-state');
    var selected = el && el.querySelector('[role="tab"][aria-selected="true"]');
    var label = tabLabel(selected);
    Array.prototype.forEach.call(document.querySelectorAll('[data-aaiko-nav-tab-label]'), function (t) {
      var on = !!(open_ && label && t.getAttribute('data-aaiko-nav-tab-label') === label);
      t.toggleAttribute('data-aaiko-nav-current', on);
    });
  }

  /* Every list back to its drawn root state. */
  function reset(el) {
    Array.prototype.forEach.call(el.querySelectorAll('[data-nav-sub]'), function (sub) {
      sub.setAttribute('hidden', '');
    });
    Array.prototype.forEach.call(el.querySelectorAll('[data-nav-list]'), function (list) {
      list.removeAttribute('hidden');
    });
    Array.prototype.forEach.call(el.querySelectorAll('[data-nav-open]'), function (btn) {
      btn.setAttribute('aria-expanded', 'false');
    });
  }

  /* Two modes, because a hover-opened menu is not a modal.
       modal - burger or a click/keyboard activation. Focus moves in, focus is
               trapped, the page is scroll-locked. Unchanged behaviour.
       hover - desktop pointer only. No focus steal, no trap, no scroll lock
               (locking the page because someone's cursor crossed a nav item
               would be hostile); it closes on pointer-leave or on scroll. */
  function open(how, trigger) {
    var el = drawer();
    if (!el) return;
    var how_ = how || 'modal';
    /* Visual state, NOT the `hidden` attribute. During a fade-out the element
       is still un-hidden while `data-state` is already gone; reading `hidden`
       here made open() think it was still open, so it skipped re-running the
       enter and only cancelled the pending hide - leaving an invisible,
       pointer-events:none overlay that could never be reopened. */
    var wasOpen = el.hasAttribute('data-state');
    clearTimeout(hideTimer);

    el.removeAttribute('hidden');
    el.setAttribute('data-aaiko-nav-mode', how_);
    placeUnderHeader(el);
    /* both modes: the header takes its page surface back while the menu is
       open (drawn 3173:20988), including over the transparent homepage hero */
    document.documentElement.setAttribute('data-aaiko-menu-open', '');

    var dialog = el.querySelector('[role="dialog"]');
    if (dialog) dialog.setAttribute('aria-modal', how_ === 'hover' ? 'false' : 'true');

    if (how_ === 'hover') {
      document.documentElement.removeAttribute('data-aaiko-nav-open');
    } else {
      document.documentElement.setAttribute('data-aaiko-nav-open', '');
    }

    if (!wasOpen) {
      /* two frames so the enter transition runs from the closed state instead
         of being collapsed into the same style recalculation */
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          el.setAttribute('data-state', 'open');
          /* Mobile enters as a single sliding panel, so the rows are already
             in place behind it - staggering them too would be a second,
             competing motion. Tab switches and branch pushes still stagger:
             they run through their own stagger() calls, not this one. */
          if (WIDE.matches) staggerActive(el);
          syncTriggers(el);
        });
      });
    } else {
      syncTriggers(el);
    }

    toggles().forEach(function (t) {
      t.setAttribute('aria-expanded', 'true');
    });

    if (how_ !== 'hover') {
      if (trigger) lastTrigger = trigger;
      /* Move focus into the dialog without lighting a focus ring on a control. */
      if (dialog) dialog.focus();
    }
  }

  function close(explicit) {
    var el = drawer();
    if (!el || !isOpen(el)) return;
    /* Already fading out. Without this, a momentum scroll fires close() dozens
       of times and each call pushes the hide timer back, so the (invisible)
       overlay lingers. */
    if (!el.hasAttribute('data-state')) return;
    var how = mode(el);

    /* An explicit dismissal (Escape, close button, scrim) must stay dismissed.
       The cursor is usually still over the trigger, and the overlay going
       pointer-events:none re-fires pointerover there, which would otherwise
       reopen the menu the user just dismissed. Cleared on pointer-leave. */
    if (explicit) dismissed = true;

    el.removeAttribute('data-state');
    document.documentElement.removeAttribute('data-aaiko-nav-open');
    document.documentElement.removeAttribute('data-aaiko-menu-open');
    syncTriggers(el);
    toggles().forEach(function (t) {
      t.setAttribute('aria-expanded', 'false');
    });

    /* `hidden` only after the fade, or the panel disappears instantly. */
    clearTimeout(hideTimer);
    hideTimer = setTimeout(
      function () {
        el.setAttribute('hidden', '');
        el.removeAttribute('data-aaiko-nav-mode');
        reset(el);
      },
      REDUCED.matches ? 0 : DUR
    );

    /* Returning focus is right for a modal the user opened deliberately; on a
       hover close it would yank the caret to the header unprompted. */
    if (how !== 'hover' && lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
    lastTrigger = null;
  }

  /* Resolve a header item to a drawer tab by LABEL, falling back to position.
     The two link lists are deliberately different (the nav bar omits Edits),
     so matching on index alone would silently point at the wrong tab as soon
     as either list is reordered in the theme editor. */
  /* Only tabs that exist on the current breakpoint: the Edits tab is
     display:none at 750 and up (it renders inline in the panel instead), and
     a header item pointing at it must fall through to navigation. Resolved by
     class + media query, NOT by rendered visibility - the drawer is `hidden`
     at rest, where every tab measures invisible. */
  var WIDE = window.matchMedia('(min-width: 750px)');
  function visibleTabs(el) {
    return Array.prototype.filter.call(el.querySelectorAll('[role="tab"]'), function (t) {
      return !(WIDE.matches && t.classList.contains('aaiko-nav__tab--inline'));
    });
  }

  function tabFor(el, trigger) {
    var tabs = visibleTabs(el);
    if (!tabs.length) return null;
    var label = trigger && trigger.getAttribute('data-aaiko-nav-tab-label');
    if (label) {
      for (var i = 0; i < tabs.length; i++) {
        if (tabLabel(tabs[i]) === label) return tabs[i];
      }
    }
    var n = parseInt(trigger && trigger.getAttribute('data-aaiko-nav-tab'), 10);
    return isNaN(n) ? null : tabs[n - 1] || null;
  }

  function openAtTab(trigger, how) {
    var el = drawer();
    if (!el) return false;
    var target = tabFor(el, trigger);
    if (!target) return false;
    open(how, how === 'hover' ? null : trigger);
    selectTab(el, target);
    return true;
  }

  /* The photo tile column follows the open tab: the girls half of the store
     has its own pair, everything else falls back to the `default` column
     (snippets/aaiko-nav-tiles.liquid). With only one column in the markup - a
     menu with no girls tab, or a merchant who has not uploaded a second pair -
     there is nothing to swap and this leaves it alone rather than hiding the
     only column there is. */
  function selectTiles(el, tab) {
    var cols = el.querySelectorAll('[data-nav-tiles]');
    if (cols.length < 2) return;
    var key = tab && tab.getAttribute('data-nav-tab');
    var match = key ? el.querySelector('[data-nav-tiles="' + key + '"]') : null;
    Array.prototype.forEach.call(cols, function (col) {
      var on = match ? col === match : col.getAttribute('data-nav-tiles') === 'default';
      if (on) col.removeAttribute('hidden');
      else col.setAttribute('hidden', '');
    });
  }

  function selectTab(el, tab) {
    Array.prototype.forEach.call(el.querySelectorAll('[role="tab"]'), function (t) {
      var on = t === tab;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.setAttribute('tabindex', on ? '0' : '-1');
      var panel = document.getElementById(t.getAttribute('aria-controls'));
      if (panel) {
        if (on) panel.removeAttribute('hidden');
        else panel.setAttribute('hidden', '');
      }
    });
    selectTiles(el, tab);
    reset(el);
    /* the newly revealed list re-runs the staggered enter, so switching tabs
       reads as a crossfade rather than a hard swap */
    staggerActive(el);
    syncTriggers(el);
  }

  document.addEventListener('click', function (event) {
    var el = drawer();

    var toggle = event.target.closest('[data-aaiko-nav-toggle]');
    if (toggle) {
      event.preventDefault();
      lastTrigger = toggle;
      if (isOpen(el)) close(true);
      else open('modal', toggle);
      return;
    }

    /* Desktop header items (WOMEN / GIRLS / EDITS). They are real links to the
       collection and the drawer hangs off them; which of the two a click gets
       depends on how the click was made.

       CLIENT REQUEST (feedback 28/08/2026): "meiden moet klikbaar zijn in de
       nav dat hij naar de meiden pagina gaat (de hoofdpagina), zelfde bij
       dames". The item used to swallow every click into the menu, so the two
       section landing pages had no entry point in the nav at all.

         mouse      the panel is already on hover, so the click is free to do
                    what the link says and navigate. This is the COS behaviour
                    the rest of the desktop menu follows.
         touch      no hover, so the first tap has to reveal the panel; a
                    second tap on the item whose tab is already open is the
                    deliberate one and navigates. data-aaiko-nav-current is
                    stamped by syncTriggers() on exactly that item.
         keyboard   Enter opens the menu modally, as before. Hover is not
                    available here either, and the drawer is the only way in;
                    the section's own page stays reachable from the
                    "Alles bekijken" row inside it.

       Progressive enhancement is unchanged: we only take over when the drawer
       and a matching tab exist, so no JS, a header list that differs from the
       drawer list, or an item with no tab all fall through to navigation. */
    var tabTrigger = event.target.closest('[data-aaiko-nav-tab]');
    if (tabTrigger && el) {
      /* detail 0 is a click synthesised by Enter/Space on the link */
      var byKeyboard = event.detail === 0;
      var goes = navigable(tabTrigger);

      if (goes && !byKeyboard) {
        /* mouse: navigate. touch: navigate only on the second tap. */
        if (FINE.matches || tabTrigger.hasAttribute('data-aaiko-nav-current')) return;
      }

      if (openAtTab(tabTrigger, 'modal')) {
        event.preventDefault();
        return;
      }
    }

    if (!el || !isOpen(el)) return;

    if (event.target.closest('[data-aaiko-nav-close]')) {
      event.preventDefault();
      close(true);
      return;
    }

    var tab = event.target.closest('[role="tab"]');
    if (tab && el.contains(tab)) {
      /* Mobile has no header row, so the tab row is the top-level navigation
         and the tab carries both of that item's jobs (see the tab row in
         snippets/aaiko-nav-drawer.liquid): an idle tab reveals its list, the
         tab you are already in goes to that section's own page. Same rule the
         header items follow on touch, one tap earlier because the drawer
         opens on the tab for the page you are on. */
      var tabUrl = tab.getAttribute('data-nav-tab-url');
      if (tabUrl && tab.getAttribute('aria-selected') === 'true') {
        window.location.assign(tabUrl);
        return;
      }
      selectTab(el, tab);
      return;
    }

    var forward = event.target.closest('[data-nav-open]');
    if (forward && el.contains(forward)) {
      var sub = document.getElementById(forward.getAttribute('aria-controls'));
      if (!sub) return;
      var root = forward.closest('[data-nav-list]');
      if (root) root.setAttribute('hidden', '');
      sub.removeAttribute('hidden');
      forward.setAttribute('aria-expanded', 'true');
      stagger(sub);
      /* focusing the sub-list is right when the user clicked into it, but on a
         hover-opened panel it would pull the caret out of the page unasked */
      if (mode(el) !== 'hover') sub.focus();
      return;
    }

    var back = event.target.closest('[data-nav-back]');
    if (back && el.contains(back)) {
      var panel = back.closest('[role="tabpanel"]');
      var current = back.closest('[data-nav-sub]');
      if (current) current.setAttribute('hidden', '');
      var list = panel && panel.querySelector('[data-nav-list]');
      if (list) {
        list.removeAttribute('hidden');
        stagger(list);
      }
      var opener =
        current && panel
          ? panel.querySelector('[aria-controls="' + current.id + '"]')
          : null;
      if (opener) {
        opener.setAttribute('aria-expanded', 'false');
        if (mode(el) !== 'hover') opener.focus();
      }
    }
  });

  document.addEventListener('keydown', function (event) {
    var el = drawer();
    if (!el || !isOpen(el)) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
      return;
    }

    var tab = event.target.closest && event.target.closest('[role="tab"]');
    if (tab && (event.key === 'ArrowRight' || event.key === 'ArrowLeft')) {
      var tabs = visibleTabs(el);
      var i = tabs.indexOf(tab);
      var next = tabs[(i + (event.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
      if (next) {
        event.preventDefault();
        selectTab(el, next);
        next.focus();
      }
      return;
    }

    if (event.key !== 'Tab') return;

    /* Only a modal traps Tab. A hover-revealed panel must let the keyboard
       continue through the page, or a mouse user's stray hover would cage the
       next keyboard user inside a menu they never opened. */
    if (mode(el) === 'hover') return;

    var nodes = Array.prototype.filter.call(el.querySelectorAll(FOCUSABLE), function (node) {
      return node.offsetParent !== null || node.getClientRects().length > 0;
    });
    if (!nodes.length) return;
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* Locale row: the drawn control is a label, the select behind it submits. */
  document.addEventListener('change', function (event) {
    var select = event.target.closest && event.target.closest('[data-aaiko-nav-locale]');
    if (!select) return;
    var form = select.closest('form');
    if (form) form.submit();
  });

  /* ---------------- Hover, desktop pointers only ----------------
     The safe zone is the header items plus the drawer itself, so travelling
     from WOMEN down into the panel never crosses "outside". pointerover /
     pointerout are used rather than mouseenter/mouseleave because they bubble,
     which keeps this delegated and re-render safe like the rest of the file. */

  /* The panel, not the whole overlay. `.aaiko-nav` is fixed inset:0, so
     treating it as the safe zone would make "outside" unreachable and the
     hover menu could never close. Leaving the panel onto the scrim is a leave.
     The open panel overlays the header, so the trip from a header item into
     the panel never passes through outside. */
  function inSafeZone(node) {
    return !!(node && node.closest && node.closest('[data-aaiko-nav-tab], .aaiko-nav__panel'));
  }
  function hoverable(event) {
    /* pointerType is '' for synthetic events in some engines; treat only an
       explicit non-mouse type as a rejection so tests and a11y tools work. */
    return FINE.matches && event.pointerType !== 'touch' && event.pointerType !== 'pen';
  }

  document.addEventListener('pointerover', function (event) {
    if (!hoverable(event)) return;
    var el = drawer();
    if (!el) return;

    var trigger = event.target.closest('[data-aaiko-nav-tab]');
    if (trigger) {
      clearTimeout(closeTimer);
      clearTimeout(hoverTimer);
      if (dismissed) return; /* stay dismissed until the pointer leaves */
      var go = function () {
        openAtTab(trigger, isOpen(el) && mode(el) !== 'hover' ? mode(el) : 'hover');
      };
      /* Synchronously at OPEN_DELAY 0: a timeout would push the open into the
         next task and reintroduce exactly the lag this removes. */
      if (OPEN_DELAY > 0) hoverTimer = setTimeout(go, OPEN_DELAY);
      else go();
      return;
    }
    /* back inside the panel: cancel a pending close */
    if (event.target.closest('.aaiko-nav__panel')) {
      clearTimeout(closeTimer);

      /* Once open, the 336px panel covers the header items, so sweeping across
         them cannot switch tabs. The drawn panel carries its own Women / Girls
         / Edits row, so that is the switcher: hovering it swaps, matching how
         the menu was opened. Hover-mode only - a modal was opened deliberately
         by click or keyboard, and should not re-tab under a passing cursor. */
      if (mode(el) === 'hover') {
        var overTab = event.target.closest('[role="tab"]');
        if (overTab && el.contains(overTab) && overTab.getAttribute('aria-selected') !== 'true') {
          selectTab(el, overTab);
        }
      }
    }
  });

  document.addEventListener('pointerout', function (event) {
    if (!hoverable(event)) return;
    var el = drawer();
    if (!el || !inSafeZone(event.target)) return;
    /* moving within the safe zone is not a leave */
    if (inSafeZone(event.relatedTarget)) return;

    dismissed = false; /* left the trigger: hover may open again */
    clearTimeout(hoverTimer);
    clearTimeout(closeTimer);
    closeTimer = setTimeout(function () {
      /* only auto-close what hover opened; a clicked-open menu stays put */
      if (mode(drawer()) === 'hover') close();
    }, CLOSE_DELAY);
  });

  /* A hover-opened menu is not scroll-locked, so scrolling should dismiss it
     rather than leave it hanging over the page. */
  function closeOnScroll() {
    if (mode(drawer()) === 'hover') close();
  }
  window.addEventListener('scroll', closeOnScroll, { passive: true });

  /* keep the desktop overlay glued to the header row across resizes */
  window.addEventListener(
    'resize',
    function () {
      var el = drawer();
      if (isOpen(el)) placeUnderHeader(el);
    },
    { passive: true }
  );

  /*
   * The nav floats over a hero, transparent and in white ink (see the overlay
   * block in sections/aaiko-hero.liquid). This reports when there is no
   * photography left behind the row, and the CSS gives the nav its surface and
   * its black ink back - white ink over the page-surface sections would be
   * unreadable.
   *
   * An IntersectionObserver on the hero does NOT work here, and that cost a
   * debug cycle: a pinned hero stays parked at the top of the viewport while
   * the rest of the page scrolls over it. It never stops intersecting, so the
   * nav would stay white forever.
   *
   * The first version of this measured the OPENING hero's flow bottom, which
   * was right while exactly one section floated under the nav.
   *
   * CLIENT FEEDBACK 26/08/2026: "logobalk op homepage wordt weer wit gevuld bij
   * scrollen". The homepage now runs three full-bleed screens back to back -
   * hero, 2-up panel, hero - so the flow bottom of the FIRST one stopped being
   * the moment the photography ends. Past it the row went white and stayed
   * white, including over the second hero, which is a solid white bar sitting
   * on a full-screen photograph.
   *
   * So it stops computing where the photography ends and asks the page what is
   * behind the row instead: one hit test, at the centre of the viewport two
   * pixels under the header's own bottom edge. Whatever would receive a click
   * there is what the nav is sitting on. Inside a hero -> float. Anything else,
   * including the panel's white padding and the gutter between its two tiles ->
   * take the surface back.
   *
   * Exact by construction, and it needs no flow arithmetic, no sentinel nodes
   * and no per-section list: it keeps working when a merchant reorders the page
   * or when the reveal above changes which box is where. The probe is a hit
   * test, i.e. the same work the browser does for one mousemove, run only on
   * scroll and resize.
   *
   * The header's measured bottom rather than a constant 44: at the top of the
   * page the announcement bar is still in flow above it, so a fixed offset
   * would probe INSIDE the header row and report the header itself.
   */
  function watchOverlay() {
    var heroes = document.querySelectorAll('#MainContent .aaiko-hero');
    if (!heroes.length) return;

    var solid = null;

    /* The topmost thing at this point that is not part of the header group
       itself - the row, the announcement bar above it and the drawer that hangs
       off it all sit over the page and would be the answer to every probe. */
    function behind(x, y) {
      var stack = document.elementsFromPoint(x, y);
      for (var i = 0; i < stack.length; i++) {
        if (!stack[i].closest('.shopify-section:has(.aaiko-header), .aaiko-nav, .aaiko-announcement')) {
          return stack[i];
        }
      }
      return null;
    }

    /* The surfaces the nav is allowed to float over in white ink: anything
       full-bleed with a dark wash under it. The hero has always been one.
       CLIENT FEEDBACK 28/08/2026: "make sure the nav stays transparent until
       section 4" - section 2 on the homepage is now the image banner, which is
       exactly that, two edge-to-edge photographs each carrying the same
       bottom-to-top black wash the hero does (blocks/aaiko-image-panel.liquid).
       The old 2-up tiles panel is deliberately NOT on this list: it is page
       surface with white padding around its photographs, so white ink over it
       would be invisible, and index.girls and page.girls still draw it.

       A list of surfaces rather than a list of positions, so "until section 4"
       stays true when a merchant reorders the page: the row asks what is
       actually behind it, not which section index it has reached. */
    var FLOAT_OVER = '.aaiko-hero, .aaiko-image-banner';

    function overHero(x, y) {
      var el = behind(x, y);
      return !!(el && el.closest && el.closest(FLOAT_OVER));
    }

    function update() {
      var head = document.querySelector('.aaiko-header');
      var box = head ? head.getBoundingClientRect() : { top: 0, bottom: 44 };
      var x = Math.round(window.innerWidth / 2);
      /* BOTH edges of the row, not one point in it. The homepage hands the row
         from a lifting panel to the hero behind it over about 30px of scroll,
         and in that window the panel's white foot is still under the top of the
         row while the hero is already under the bottom of it. One probe at the
         bottom edge would go white-on-white for those 30px. */
      var next = !(overHero(x, box.top + 2) && overHero(x, box.bottom - 2));
      if (next === solid) return;
      solid = next;
      for (var i = 0; i < heroes.length; i++) {
        heroes[i].toggleAttribute('data-nav-solid', next);
      }
    }

    update();

    /* Both listeners: Horizon moves the scroll container by breakpoint, so
       binding to whichever one scrolls today breaks on the first resize across
       990px. */
    window.addEventListener('scroll', update, { passive: true });
    document.addEventListener(
      'scroll',
      function (event) {
        if (event.target && event.target.classList && event.target.classList.contains('page-wrapper')) {
          update();
        }
      },
      { passive: true, capture: true }
    );
    window.addEventListener('resize', update, { passive: true });
    /* The heroes carry photography, so the boxes are not final at
       DOMContentLoaded. */
    window.addEventListener('load', update);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchOverlay);
  } else {
    watchOverlay();
  }
  document.addEventListener(
    'scroll',
    function (event) {
      if (event.target && event.target.classList && event.target.classList.contains('page-wrapper')) {
        closeOnScroll();
      }
    },
    { passive: true, capture: true }
  );
})();
